import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { BreadCrumb } from "primereact/breadcrumb";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SmilesView from "../../../../app/common/SmilesView/SmilesView";
import SectionHeading from "../../../../app/common/SectionHeading/SectionHeading";
import SmilesDrawer from "smiles-drawer";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import agent from "../../../../app/api/agent";
import "./ReferenceCompounds.css";

const ReferenceCompounds = ({ id: projectId }) => {
  const navigate = useNavigate();
  const rootStore = useContext(RootStoreContext);
  const { selectedProject } = rootStore.projectStore;
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get("groupId");

  const toast = useRef(null);
  const fileInputRef = useRef(null);
  const [compounds, setCompounds] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Add row dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addId, setAddId] = useState("");
  const [addSmiles, setAddSmiles] = useState("");
  const [addExtra, setAddExtra] = useState("{}");
  const [saving, setSaving] = useState(false);

  // Column management
  const [showColumnDialog, setShowColumnDialog] = useState(false);
  const [editingColumns, setEditingColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");

  // Table height
  const tableWrapperRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState("flex");
  const [rowHeight, setRowHeight] = useState(50);
  const [displayStructurePreview, setDisplayStructurePreview] = useState(false);
  const [structurePreviewSmiles, setStructurePreviewSmiles] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [localRows, setLocalRows] = useState([]);
  const editSnapshot = useRef(null);

  const breadcrumbItems = [
    { label: "Project Management", command: () => navigate("/pm/") },
    { label: "Projects", command: () => navigate("/pm/project/") },
    { label: selectedProject?.projectName || "Project" },
    { label: "Reference Compounds" },
  ];

  // ── Data Fetching ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [data, cols] = await Promise.all([
        agent.ReferenceCompounds.list(projectId, groupId),
        agent.ReferenceCompounds.listColumns(projectId),
      ]);
      setCompounds(data || []);
      setColumns(cols || []);
    } catch (err) {
      console.error("Failed to load reference compounds", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, groupId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync compounds → localRows
  useEffect(() => {
    setLocalRows(compounds.map((r) => ({ ...r, extra: r.extraData ? JSON.parse(r.extraData) : {} })));
  }, [compounds]);

  // ── Inline editing ──
  const updateField = (index, field, value) => {
    setLocalRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateExtraField = (index, colName, value) => {
    setLocalRows((prev) => {
      const updated = [...prev];
      const extra = { ...(updated[index].extra || {}), [colName]: value };
      updated[index] = { ...updated[index], extra };
      return updated;
    });
  };

  const startEditing = (row, index) => {
    editSnapshot.current = JSON.parse(JSON.stringify(row));
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    if (editSnapshot.current !== null && editingIndex !== null) {
      setLocalRows((prev) => {
        const updated = [...prev];
        updated[editingIndex] = editSnapshot.current;
        return updated;
      });
    }
    editSnapshot.current = null;
    setEditingIndex(null);
  };

  const handleSaveRow = async (row, index) => {
    try {
      await agent.ReferenceCompounds.save(projectId, {
        id: row.id,
        compoundId: row.compoundId,
        smile: row.smile,
        extraData: JSON.stringify(row.extra || {}),
        referenceGroupId: groupId || null,
      });
      toast.current.show({ severity: "success", summary: "Saved", life: 2000 });
      setEditingIndex(null);
      editSnapshot.current = null;
      fetchData();
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Save failed", life: 3000 });
    }
  };

  // ── Table height calculation ──
  useEffect(() => {
    const updateHeight = () => {
      if (tableWrapperRef.current) {
        const rect = tableWrapperRef.current.getBoundingClientRect();
        const h = window.innerHeight - rect.top - 60;
        if (h > 200) setTableScrollHeight(`${h}px`);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [compounds]);

  // ── Canvas auto-sizing ──
  const redrawAllCanvases = useCallback(() => {
    document.querySelectorAll(".ref-structure-canvas").forEach((el) => {
      const smiles = el.dataset.smiles;
      if (!smiles) return;
      const parent = el.parentElement;
      const cellWidth = parent?.offsetWidth || 200;
      const cellHeight = parent?.offsetHeight || 60;
      const w = Math.max(cellWidth - 16, 60);
      const h = Math.max(cellHeight - 4, 36);
      const dpr = window.devicePixelRatio || 1;
      el.width = w * dpr;
      el.height = h * dpr;
      el.style.width = w + "px";
      el.style.height = h + "px";
      try {
        const drawer = new SmilesDrawer.Drawer({
          width: w * dpr, height: h * dpr,
          bondThickness: 1.0, shortBondLength: 1.0,
          fontSizeScale: 0.8, atomVisualization: "default",
          isomericHighlight: true, explicitHydrogens: false,
        });
        SmilesDrawer.parse(
          smiles,
          (tree) => drawer.draw(tree, el.id, "light", false),
          (err) => console.error("SmilesDrawer error:", err)
        );
      } catch (e) {
        console.error("SmilesDrawer render error:", e);
      }
    });
  }, []);

  useEffect(() => {
    if (compounds.length > 0) {
      const t1 = setTimeout(() => redrawAllCanvases(), 100);
      const t2 = setTimeout(() => redrawAllCanvases(), 500);
      const t3 = setTimeout(() => redrawAllCanvases(), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [compounds, rowHeight, redrawAllCanvases]);

  // ── Delete ──
  const handleDelete = async (row, index) => {
    if (!window.confirm("Delete this reference compound?")) return;
    try {
      await agent.ReferenceCompounds.delete(row.id);
      toast.current.show({ severity: "success", summary: "Deleted", life: 2000 });
      fetchData();
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to delete", life: 3000 });
    }
  };

  // ── Actions Template ──
  const actionsTemplate = (row, options) => {
    const idx = options.rowIndex;
    const isEditing = editingIndex === idx;
    return (
      <div className="flex gap-1" style={{ justifyContent: "center" }}>
        {isEditing ? (
          <>
            <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
              tooltip="Save" onClick={() => handleSaveRow(row, idx)} />
            <Button icon="pi pi-times" className="p-button-rounded p-button-secondary p-button-text p-button-sm"
              tooltip="Cancel" onClick={cancelEditing} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
              tooltip="Delete" onClick={() => handleDelete(row, idx)} />
          </>
        ) : (
          <Button icon="pi pi-pencil" className="p-button-rounded p-button-info p-button-text p-button-sm"
            tooltip="Edit inline" onClick={() => startEditing(row, idx)} />
        )}
      </div>
    );
  };

  // ── Add Row ──
  const handleAddRow = async () => {
    if (!addId.trim() || !addSmiles.trim()) {
      toast.current.show({ severity: "warn", summary: "ID and SMILES are required", life: 2000 });
      return;
    }
    setSaving(true);
    try {
      let parsedExtra = {};
      try { parsedExtra = JSON.parse(addExtra); } catch { parsedExtra = {}; }
      await agent.ReferenceCompounds.save(projectId, {
        compoundId: addId.trim(),
        smile: addSmiles.trim(),
        extraData: JSON.stringify(parsedExtra),
        referenceGroupId: groupId || null,
      });
      toast.current.show({ severity: "success", summary: "Row added", life: 2000 });
      setShowAddDialog(false);
      setAddId(""); setAddSmiles(""); setAddExtra("{}");
      fetchData();
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Failed to add row", life: 3000 });
    } finally { setSaving(false); }
  };

  // ── Manage Columns ──
  const openColumnDialog = () => {
    setEditingColumns(columns.map((c) => ({
      id: c.id,
      columnName: c.columnName,
      displayName: c.displayName,
      dataType: c.dataType || "string",
      sortOrder: c.sortOrder,
    })));
    setNewColumnName("");
    setShowColumnDialog(true);
  };

  const addCustomColumn = () => {
    if (!newColumnName.trim()) return;
    const colName = newColumnName.trim().toLowerCase().replace(/\s+/g, "_");
    setEditingColumns((prev) => [
      ...prev,
      { id: null, columnName: colName, displayName: newColumnName.trim(), dataType: "string", sortOrder: prev.length },
    ]);
    setNewColumnName("");
  };

  const removeCustomColumn = (index) => {
    setEditingColumns((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveColumns = async () => {
    try {
      await agent.ReferenceCompounds.saveColumns(projectId, { columns: editingColumns });
      setColumns(editingColumns);
      setShowColumnDialog(false);
      toast.current.show({ severity: "success", summary: "Columns saved", life: 2000 });
    } catch {
      toast.current.show({ severity: "error", summary: "Failed to save columns", life: 3000 });
    }
  };

  // ── CSV Import ──
  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      await agent.ReferenceCompounds.importCsv(projectId, file);
      toast.current.show({ severity: "success", summary: "Import successful", life: 2000 });
      fetchData();
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Import failed", life: 3000 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Extra Data display ──
  const extraBodyTemplate = (col) => (row) => {
    if (!row.extraData) return "";
    try {
      const parsed = JSON.parse(row.extraData);
      return parsed[col.columnName] != null ? String(parsed[col.columnName]) : "";
    } catch { return ""; }
  };

  return (
    <div className="flex flex-column w-full fadein animation-duration-500 subtable-wrapper" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Force center alignment for custom columns */}
      <style>{`
        .subtable-wrapper .p-datatable-scrollable-body .p-datatable-tbody > tr > td {
          text-align: center !important;
          vertical-align: middle !important;
        }
        .subtable-wrapper .p-datatable-scrollable-body .p-datatable-tbody > tr > td:first-child,
        .subtable-wrapper .p-datatable-scrollable-body .p-datatable-tbody > tr > td:nth-child(3),
        .subtable-wrapper .p-datatable-scrollable-body .p-datatable-tbody > tr > td:last-child {
          text-align: left !important;
        }
      `}</style>
      <Toast ref={toast} />
      <div className="flex w-full pb-2">
        <BreadCrumb model={breadcrumbItems} />
      </div>
      <SectionHeading
        icon="icon icon-common icon-book"
        heading="Reference Compounds"
        color="#1e1b4b"
        textColor="#ffffff"
        customButtons={[
          { label: "Add Row", icon: "pi pi-plus", action: () => setShowAddDialog(true), className: "p-button-sm subtable-toolbar-btn" },
          { label: "Manage Columns", icon: "pi pi-cog", action: () => openColumnDialog(), className: "p-button-sm subtable-toolbar-btn" },
          { label: "Import CSV", icon: "pi pi-upload", action: () => fileInputRef.current?.click(), className: "p-button-sm subtable-toolbar-btn" },
        ]}
      />
      <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleImportCsv} />

      <div ref={tableWrapperRef} className="flex w-full" style={{ flex: 1, minHeight: 0 }}>
        <DataTable
          value={localRows}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          className="w-full"
          emptyMessage="No reference compounds found."
          stripedRows
          size="small"
          scrollable
          scrollHeight={tableScrollHeight}
          resizableColumns
          columnResizeMode="expand"
        >
          <Column field="compoundId" header="ID" sortable style={{ minWidth: 120 }}
            headerStyle={{ textAlign: "center" }}
            body={(row) => <span className="font-semibold" style={{ fontSize: "13px", color: "#1e293b" }}>{row.compoundId || "—"}</span>}
          />
          <Column header="Structure" style={{ minWidth: 90, width: 110 }}
            headerStyle={{ textAlign: "center" }}
            bodyStyle={{ textAlign: "center", display: "flex", justifyContent: "center" }}
            body={(row) => {
              if (!row.smile) return <span style={{ color: "#cbd5e1", fontSize: "0.75rem" }}>—</span>;
              return (
                <div style={{ position: "relative", width: "100%", height: rowHeight, minHeight: rowHeight }}>
                  <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <canvas
                      id={`ref-structure-${row.id}`}
                      className="ref-structure-canvas"
                      data-smiles={row.smile}
                      style={{ display: "block", cursor: "pointer" }}
                      onClick={() => { setStructurePreviewSmiles(row.smile); setDisplayStructurePreview(true); }}
                    />
                  </div>
                  <div className="subtable-row-resize-handle"
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const startY = e.clientY;
                      const startH = rowHeight;
                      const onMove = (ev) => { setRowHeight(Math.max(50, Math.min(300, startH + ev.clientY - startY))); };
                      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  />
                </div>
              );
            }}
          />
          <Column field="smile" header="SMILES" sortable style={{ minWidth: 130 }}
            body={(row, options) => {
              const idx = options.rowIndex;
              if (editingIndex === idx) {
                return (
                  <InputText value={row.smile || ""}
                    onChange={(e) => updateField(idx, "smile", e.target.value)}
                    style={{ width: "100%", fontSize: "0.8rem", fontFamily: "monospace" }} />
                );
              }
              return (
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: "0.8rem", color: "#334155" }}>
                  {row.smile || "—"}
                </span>
              );
            }}
          />
          {/* Dynamic custom columns */}
          {columns.map((col) => (
            <Column
              key={col.columnName}
              header={col.displayName || col.columnName}
              sortable
              style={{ minWidth: 100 }}
              bodyStyle={{ textAlign: "center" }}
              body={(row, options) => {
                const idx = options.rowIndex;
                const val = row.extra ? row.extra[col.columnName] : "";
                if (editingIndex === idx) {
                  return (
                    <InputText value={val || ""}
                      onChange={(e) => updateExtraField(idx, col.columnName, e.target.value)}
                      style={{ width: "100%", fontSize: "0.8rem" }} />
                  );
                }
                return <span>{val != null ? String(val) : ""}</span>;
              }}
            />
          ))}
          <Column header="Actions" body={actionsTemplate} style={{ width: 90 }} />
        </DataTable>
      </div>

      {/* ── Structure Preview Dialog ── */}
      <Dialog
        header="Molecular Structure"
        visible={displayStructurePreview}
        style={{ width: "480px" }}
        modal
        onHide={() => { setDisplayStructurePreview(false); setStructurePreviewSmiles(null); }}
        footer={
          <Button label="Close" icon="pi pi-times" className="p-button-text"
            onClick={() => { setDisplayStructurePreview(false); setStructurePreviewSmiles(null); }} />
        }
      >
        {structurePreviewSmiles && (
          <div className="flex justify-content-center p-3">
            <SmilesView smiles={structurePreviewSmiles} width={400} height={350} />
          </div>
        )}
      </Dialog>

      {/* ── Add Row Dialog ── */}
      <Dialog header="Add Reference Compound" visible={showAddDialog}
        style={{ width: "480px" }} modal draggable={false}
        onHide={() => { setShowAddDialog(false); setAddId(""); setAddSmiles(""); setAddExtra("{}"); }}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm"
              onClick={() => { setShowAddDialog(false); setAddId(""); setAddSmiles(""); setAddExtra("{}"); }} />
            <Button label="Save" icon="pi pi-check" className="p-button-sm subtable-primary-btn"
              loading={saving} onClick={handleAddRow} />
          </div>
        }
      >
        <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Compound ID *</label>
            <InputText value={addId} onChange={(e) => setAddId(e.target.value)}
              placeholder="e.g. REF-001" style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>SMILES *</label>
            <InputText value={addSmiles} onChange={(e) => setAddSmiles(e.target.value)}
              placeholder="e.g. CCO" style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Custom Fields (JSON)</label>
            <InputTextarea value={addExtra} onChange={(e) => setAddExtra(e.target.value)}
              rows={3} style={{ width: "100%", fontSize: "0.8rem", fontFamily: "monospace" }}
              placeholder='{"column1": "value1"}' />
          </div>
        </div>
      </Dialog>

      {/* ── Manage Columns Dialog ── */}
      <Dialog header="Manage Columns" visible={showColumnDialog}
        style={{ width: "450px" }} modal draggable={false}
        onHide={() => setShowColumnDialog(false)}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => setShowColumnDialog(false)} />
            <Button label="Save Columns" icon="pi pi-check" className="p-button-sm subtable-primary-btn" onClick={handleSaveColumns} />
          </div>
        }
      >
        <div className="flex flex-column gap-2">
          {editingColumns.map((col, i) => (
            <div key={i} className="flex align-items-center gap-2 p-2 border-round" style={{ background: i % 2 === 0 ? "#f8fafc" : "transparent" }}>
              <span style={{ flex: 1, fontSize: "0.875rem" }}>{col.displayName || col.columnName}</span>
              <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm" onClick={() => removeCustomColumn(i)} />
            </div>
          ))}
          <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 8, paddingTop: 8 }}>
            <div className="flex gap-2">
              <InputText placeholder="New column name" value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)}
                style={{ flex: 1 }} />
              <Button label="Add" icon="pi pi-plus" className="p-button-sm" onClick={addCustomColumn} disabled={!newColumnName.trim()} />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default observer(ReferenceCompounds);
